import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useReactToPrint } from "react-to-print";
import { Box } from "@mui/material";

import { PageLayout } from "../../layouts/PageLayout";
import { CardLayout, Invoice, PageHeader } from "../../components/v3";
import { DownloadAndPrint } from "../../components/DownloadAndPrint";
import { get_date } from "../../components/generalComponets";
import { HumanResourceService, ProjectService } from "../../services";
import { getDateAndTime } from "../../utils/formatters";

interface UserInfo {
  firstName: string;
  lastName: string;
}

interface WorkerParticular {
  particularId: string;
  name: string;
  rate: string | number;
  quantity: string | number;
}

interface WorkDetailsData {
  isGroupWork?: boolean;
  date?: string;
  createdBy?: UserInfo;
  createdAt?: string;
  updatedBy?: UserInfo;
  updatedAt?: string;
  worker?: { name: string };
  project?: { name: string };
  overtimeHours?: number;
  overtimeRate?: number;
  wage?: string | number;
  particulars?: WorkerParticular[];
  description?: string;
  projectStage?: {
    stageId: number;
    name?: string;
  };
  totalAmount?: string | number;
}

export const WorkDetails = () => {
  const [invoiceData, setInvoiceData] = useState<Partial<WorkDetailsData>>({});
  const [particularDetails, setParticularDetails] = useState([]);

  const { workId } = useParams();
  const printRef = useRef<HTMLDivElement>(null);
  const particularsMap = useMemo(() => {
    const map = {};
    particularDetails?.forEach((item) => {
      map[String(item.id)] = item;
    });
    return map;
  }, [particularDetails]);

  useEffect(() => {
    fetchData(workId);
  }, [workId]);

  const fetchData = async (id) => {
    try {
      const { data: response } = await HumanResourceService.getWorkById(id);

      if (response?.projectStage?.stageId) {
        try {
          const { data: stage } = await ProjectService.getStageById(response.projectStage.stageId);

          response.projectStage.name = stage?.name;
        } catch (err) {
          console.error("Error fetching stage name:", err);
        }
      }

      setInvoiceData(response);

      if (response?.isGroupWork && response?.particulars?.length > 0) {
        const ids = response.particulars
          .map((p) => p.particularId)
          .filter(Boolean)
          .join(",");
        if (ids) {
          const { data: particularsResponse } = await HumanResourceService.getWorkerParticulars({ ids });
          setParticularDetails(particularsResponse?.rows || []);
        }
      }
    } catch (error) {
      toast.error("Failed to fetch work details.");
      console.error("Error fetching work details:", error);
    }
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
  });
  return (
    <PageLayout>
      {/* header section */}
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <PageHeader title="Work Details" />
      </Box>

      {/* table section */}
      <Box sx={{ mt: -9, zIndex: 2, position: "relative" }}>
        <CardLayout>
          <div ref={printRef}>
            <Invoice
              title={` ${invoiceData?.isGroupWork ? "Group Work" : "Work"} Details `}
              sectionA={[{ label: "Date", value: get_date(invoiceData?.date, "DD-MM-YYYY") }]}
              sectionB={[
                {
                  label: "Created By",
                  value: invoiceData?.createdBy
                    ? `${invoiceData?.createdBy?.firstName} ${invoiceData?.createdBy?.lastName} (${getDateAndTime(
                        invoiceData?.createdAt,
                      )})`
                    : "N/A",
                },
                invoiceData?.updatedBy && {
                  label: "Last Updated By",
                  value: `${invoiceData?.updatedBy?.firstName ?? ""} ${invoiceData?.updatedBy?.lastName ?? ""} (${getDateAndTime(
                    invoiceData?.updatedAt,
                  )})`,
                },
              ].filter(Boolean)}
              sectionC={[
                { label: "Worker", value: invoiceData?.worker?.name },
                { label: "Stage", value: invoiceData?.projectStage?.name },
                { label: "Project", value: invoiceData?.project?.name },
              ]}
              sectionD={
                invoiceData?.isGroupWork
                  ? []
                  : [
                      {
                        label: "Overtime",
                        value: invoiceData?.overtimeHours ? `₹ ${invoiceData?.overtimeHours * invoiceData?.overtimeRate}` : "-",
                      },
                      { label: "Wage", value: invoiceData?.wage ? `₹ ${invoiceData?.wage}` : "-" },
                      { label: "Description", value: invoiceData?.description || "-" },
                    ]
              }
              columns={
                invoiceData?.isGroupWork
                  ? [
                      { id: "name", label: "Particulars" },
                      { id: "rate", label: "Rate" },
                      { id: "quantity", label: "Quantity" },
                      { id: "total", label: "Total" },
                    ]
                  : []
              }
              rows={
                invoiceData?.isGroupWork
                  ? invoiceData?.particulars?.map((p) => {
                      const backendParticular = particularsMap[String(p.particularId)];
                      return {
                        name: backendParticular?.name || p.name,
                        rate: `₹ ${p.rate}`,
                        quantity: p.quantity,
                        total: `₹ ${Number(p.rate || 0) * Number(p.quantity || 0)}`,
                      };
                    })
                  : []
              }
              total={invoiceData?.totalAmount}
            />
          </div>
        </CardLayout>
      </Box>

      <DownloadAndPrint handlePrint={handlePrint} printRef={printRef} fileName={"WorkDetails.pdf"} />
    </PageLayout>
  );
};
